import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Lock, ArrowLeft, Check, KeyRound } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${window.location.origin}/api`;
const LOGO_URL = "/api/files/jsfragrance/products/be3c5fd6-5c5e-4e28-9eca-fed1e5e06eea.png";

export default function ForgotPasswordPage() {
  const [step, setStep] = useState('email'); // email | otp | password | done
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleRequestOTP = async (e) => {
    e.preventDefault();
    if (!email.trim()) { toast.error('Enter your email'); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/forgot-password`, { email: email.trim() });
      if (res.data.token) setResetToken(res.data.token);
      toast.success('Verification code sent to your email');
      setStep('otp');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Something went wrong');
    }
    setLoading(false);
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) { toast.error('Enter the 6-digit code'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/verify-otp`, { token: resetToken, otp });
      toast.success('Code verified!');
      setStep('password');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid code');
    }
    setLoading(false);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    if (newPassword !== confirmPassword) { toast.error('Passwords do not match'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token: resetToken, new_password: newPassword });
      toast.success('Password reset successfully!');
      setStep('done');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password');
    }
    setLoading(false);
  };

  const titles = {
    email: 'Forgot Password', otp: 'Enter Code', password: 'New Password', done: 'Password Reset',
  };

  return (
    <div data-testid="forgot-password-page" className="min-h-screen flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <Link to="/"><img src={LOGO_URL} alt="JS Fragrance" className="h-16 w-auto mx-auto mb-4" /></Link>
          <h1 className="text-2xl text-white font-light" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            {titles[step]}
          </h1>
        </div>

        {/* Step 1: Email */}
        {step === 'email' && (
          <form onSubmit={handleRequestOTP} className="space-y-4">
            <p className="text-white/40 text-sm font-sans text-center mb-4">
              Enter your email and we'll send a 6-digit verification code.
            </p>
            <div className="relative">
              <Mail className="absolute left-0 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
              <input type="email" placeholder="Email address" value={email}
                onChange={e => setEmail(e.target.value)}
                className="w-full bg-transparent border-b border-white/20 text-white py-3 pl-6 text-base focus:border-[#D4AF37] focus:outline-none transition-colors placeholder:text-white/30"
                data-testid="forgot-email-input" required />
            </div>
            <button type="submit" disabled={loading}
              className="w-full bg-[#D4AF37] text-black py-3 text-sm uppercase tracking-[0.15em] font-medium hover:bg-[#F3E5AB] transition-colors disabled:opacity-50"
              data-testid="forgot-submit-btn">
              {loading ? 'Sending...' : 'Send Verification Code'}
            </button>
          </form>
        )}

        {/* Step 2: OTP */}
        {step === 'otp' && (
          <form onSubmit={handleVerifyOTP} className="space-y-4">
            <p className="text-white/40 text-sm font-sans text-center mb-4">
              We sent a 6-digit code to <span className="text-white">{email}</span>. Check your inbox.
            </p>
            <div className="relative">
              <KeyRound className="absolute left-0 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
              <input type="text" placeholder="000000" value={otp} maxLength={6}
                onChange={e => setOtp(e.target.value.replace(/\D/g, ''))}
                className="w-full bg-transparent border-b-2 border-white/20 text-white text-center text-2xl py-4 pl-6 font-mono tracking-[0.5em] focus:border-[#D4AF37] focus:outline-none placeholder:text-white/15"
                data-testid="otp-input" required autoFocus />
            </div>
            <p className="text-white/20 text-xs text-center font-sans">Code expires in 5 minutes</p>
            <button type="submit" disabled={loading || otp.length !== 6}
              className="w-full bg-[#D4AF37] text-black py-3 text-sm uppercase tracking-[0.15em] font-medium hover:bg-[#F3E5AB] transition-colors disabled:opacity-50"
              data-testid="verify-otp-btn">
              {loading ? 'Verifying...' : 'Verify Code'}
            </button>
            <button type="button" onClick={() => { setOtp(''); handleRequestOTP({ preventDefault: () => {} }); }}
              className="w-full text-white/30 text-sm hover:text-white/50 transition-colors font-sans"
              data-testid="resend-otp-btn">
              Resend Code
            </button>
          </form>
        )}

        {/* Step 3: New Password */}
        {step === 'password' && (
          <form onSubmit={handleResetPassword} className="space-y-4">
            <p className="text-white/40 text-sm font-sans text-center mb-4">
              Create your new password.
            </p>
            <div className="relative">
              <Lock className="absolute left-0 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
              <input type="password" placeholder="New Password (min 6 chars)" value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                className="w-full bg-transparent border-b border-white/20 text-white py-3 pl-6 text-base focus:border-[#D4AF37] focus:outline-none transition-colors placeholder:text-white/30"
                data-testid="new-password-input" required />
            </div>
            <div className="relative">
              <Lock className="absolute left-0 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
              <input type="password" placeholder="Confirm New Password" value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                className="w-full bg-transparent border-b border-white/20 text-white py-3 pl-6 text-base focus:border-[#D4AF37] focus:outline-none transition-colors placeholder:text-white/30"
                data-testid="confirm-password-input" required />
            </div>
            <button type="submit" disabled={loading}
              className="w-full bg-[#D4AF37] text-black py-3 text-sm uppercase tracking-[0.15em] font-medium hover:bg-[#F3E5AB] transition-colors disabled:opacity-50"
              data-testid="reset-password-btn">
              {loading ? 'Resetting...' : 'Reset Password'}
            </button>
          </form>
        )}

        {/* Step 4: Done */}
        {step === 'done' && (
          <div className="text-center">
            <div className="w-16 h-16 bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-[#D4AF37]" />
            </div>
            <p className="text-white/60 text-sm font-sans mb-6">Your password has been reset. You can now log in with your new password.</p>
            <Link to="/login"
              className="inline-block bg-[#D4AF37] text-black px-8 py-3 text-sm uppercase tracking-[0.15em] font-medium hover:bg-[#F3E5AB] transition-colors"
              data-testid="back-to-login-btn">
              Back to Login
            </Link>
          </div>
        )}

        {step !== 'done' && (
          <p className="text-center mt-6">
            <Link to="/login" className="text-white/40 text-sm hover:text-white/60 transition-colors flex items-center justify-center gap-2 font-sans">
              <ArrowLeft className="w-3 h-3" /> Back to Login
            </Link>
          </p>
        )}
      </div>
    </div>
  );
}
